import {
  CollectionFilter,
  DomClassList,
  ResourceInfoColumn,
  ResourceModel,
  SchedulerEventModel,
  SchedulerProConfig,
  EventModelConfig,
  EventModel,
  DateHelper,
} from "@bryntum/schedulerpro";
import { BryntumSchedulerProProps } from "@bryntum/schedulerpro-react";
import { SimplifiedPlannerProcess, baseSchedulerProConfig } from "@oculai/planner";
import { format, intervalToDuration, subDays } from "date-fns";
import { TagGroupedProcess } from "@/types/Process";

export type ProcessResourceModel = Partial<ResourceModel> & {
  component?: TagGroupedProcess["tags"];
  index?: number;
  process?: string;
  processElement?: string;
};

export type ProcessEventModel = Partial<EventModelConfig> & {
  processElement: string;
  processes: SimplifiedPlannerProcess[];
};

const escapeRegExp = (text: string) => {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

const generateOutagesTooltip = (
  outages: { start_time: Date; end_time: Date; camera_name: string }[] | null,
  t: (key: string) => string,
) => {
  if (!outages || outages.length === 0) {
    return "";
  }
  return `
    <div class="flex flex-col mt-4">
      <h4 class="border-b pb-1 text-base flex gap-2 justify-between items-center">
        ${t("analytics.planner.outages")}
      </h4>
      <div class="flex gap-2 flex-wrap mt-2" style="max-width: 400px">
            ${outages
              .map(
                (outage) => `
              <div class="flex text-sm text-white px-1 py-0.5 rounded w-min whitespace-nowrap bg-red-300">
                <span>${outage.camera_name}:&nbsp;</span>
                <span>${format(outage.start_time, "HH:mm")}-${format(
                  outage.end_time,
                  "HH:mm",
                )}</span>
              </div>
            `,
              )
              .join("")}
    </div>
  `;
};

const generateHolidayTooltip = (holidayInfo: {
  name: string;
  start_date: Date;
  end_date: Date;
  type: string;
}) => {
  if (holidayInfo) {
    const timeRange =
      holidayInfo.start_date.getTime() === holidayInfo.end_date.getTime()
        ? format(holidayInfo.start_date, "dd.MM.yyyy")
        : `${format(holidayInfo.start_date, "dd.MM.yyyy")}-${format(
            holidayInfo.end_date,
            "dd.MM.yyyy",
          )}`;

    return `
      <div style="${
        holidayInfo.type === "disturbance"
          ? "background-color: #f5e3be; color: #9c7018"
          : "background-color: #bfdbfe4c; color: #1d4ed8"
      }; border-radius: 4px; width: min-content; padding: 2px 4px; font-size: 14px; white-space: nowrap;">
        ${holidayInfo.name} (${timeRange})
      </div>
    `;
  }
  return "";
};

export const getSchedulerProConfig = ({
  locale,
  projectName,
  t,
}: {
  locale: string;
  projectName: string;
  t: (key: string) => string;
}): Partial<BryntumSchedulerProProps> => {
  const colors = {
    building: {
      text: "text-gray-100",
      txtDark: "text-gray-100",
      bgLight: "bg-gray-400",
      bgDark: "bg-yellow-500",
    },
    level: {
      text: "text-gray-700",
      txtDark: "text-gray-100",
      bgLight: "bg-gray-300",
      bgDark: "bg-yellow-400",
    },
    section: {
      text: "text-gray-700",
      txtDark: "text-gray-100",
      bgLight: "bg-gray-200",
      bgDark: "bg-yellow-300",
    },
    component: {
      text: "text-gray-700",
      txtDark: "text-gray-100",
      bgLight: "bg-gray-100",
      bgDark: "bg-yellow-100",
    },
  } as const;

  return {
    ...(baseSchedulerProConfig("process", locale, t) as Partial<SchedulerProConfig>),
    columns: [
      {
        text: "#",
        field: "index",
        width: 30,
        htmlEncode: false,
        renderer({ record }: { record: ProcessResourceModel }) {
          return `<span class="text-sm">${record.index}</span>`;
        },
        filterable: false,
      },
      {
        text: t("analytics.processes.tags"),
        field: "component",
        autoWidth: true,
        autoHeight: true,
        minWidth: 220,
        htmlEncode: false,
        type: "resourceInfo",
        cls: "plannerColFilter",
        filterable: {
          id: "component",
          filterFn({
            record,
            property,
            value,
          }: {
            property: string;
            record: Record<string, TagGroupedProcess["tags"]>;
            value: string;
          }) {
            const tags = record[property as keyof typeof record];

            const inputs = value.split(" ").filter(Boolean);

            return inputs.every((input) => {
              return Object.values(tags || {}).some((tag) =>
                (tag?.toLowerCase() || "").includes(input?.toLowerCase()),
              );
            });
          },
        },
        renderer({
          value,
          column,
        }: {
          value: TagGroupedProcess["tags"];
          column: ResourceInfoColumn;
        }) {
          const filterValues: string[] =
            (column as ResourceInfoColumn & { $filter: CollectionFilter }).$filter?.value
              ?.split(" ")
              ?.filter(Boolean) || [];

          const checkForFullMatch = (name: string, filterValues: string[], i: number) => {
            const wordsCount = name.split(" ").length;

            if (wordsCount === 1) {
              return name.toLowerCase() === filterValues[i].toLowerCase();
            }

            return name.split(" ").every((word, j) => {
              return word.toLowerCase() === filterValues.at(i + j)?.toLowerCase();
            });
          };

          const html = `
          <div style="display:flex;gap:6px">
            ${(Object.entries(value || {}) as [keyof typeof value, string][])
              .map(([type, name]) => {
                let isFullMatch = false;
                let modifiedName = name;

                filterValues.forEach((filterValue, i) => {
                  const index = modifiedName.toLowerCase().indexOf(filterValue.toLowerCase());
                  const tagCloseIndex = modifiedName.lastIndexOf(">", index);
                  const openTagIndex = modifiedName.lastIndexOf("<", index);
                  isFullMatch = isFullMatch || checkForFullMatch(name, filterValues, i);

                  if (tagCloseIndex >= openTagIndex && !isFullMatch) {
                    if (["*", "+", "?"].includes(filterValue)) {
                      filterValue = `\\${filterValue}`;
                    }

                    modifiedName = modifiedName.replace(
                      new RegExp(`(${escapeRegExp(filterValue)})`, "gi"),
                      '<span class="bg-yellow-300">$1</span>',
                    );
                  }
                });

                const triggerInput = `(()=>{
                  const input = document.querySelector('.plannerColFilter input');
                  input.value?.includes('${name}') ? 
                    input.value = input.value.replace('${name}', '').split(' ').filter(Boolean).join(' ') :
                    input.value += ' ${name}';
                  input.dispatchEvent(new Event('input'));
                })()`;

                return `<div class="px-2.5 py-1 my-1 cursor-pointer rounded-full align-middle text-xs whitespace-nowrap
                  ${isFullMatch ? colors[type].bgDark : colors[type].bgLight} ${colors[type].text}"
                  ondblclick="${triggerInput}"}>
                    ${isFullMatch ? name : modifiedName}
                  </div>`;
              })
              .join("")}
          </div>`;
          return html;
        },
      },
      {
        text: t("analytics.processes.processes"),
        field: "process",
        autoWidth: true,
        autoHeight: true,
        hidden: true,
        cls: "plannerColFilter",
        minWidth: 220,
        htmlEncode: false,
        renderer({ record }: { record: ProcessResourceModel }) {
          return `<span class="font-normal text-sm">${record.getData?.("process")}</span>`;
        },
        filterable: {
          id: "process",
          filterFn({
            record: {
              originalData: { process },
            },
            value,
          }: {
            record: { originalData: { process: string } };
            value: string;
          }) {
            const inputs = value.split(" ").filter(Boolean);

            return inputs.some((input) => {
              return process?.toLowerCase().includes(input?.toLowerCase());
            });
          },
        },
      },
    ],
    eventRenderer({
      eventRecord,
      renderData,
    }: {
      eventRecord: SchedulerEventModel;
      renderData: {
        eventColor: string;
        cls: DomClassList;
      };
    }) {
      if (eventRecord.getData("type")?.startsWith("process_")) {
        renderData.cls.add(
          `plannerProcessEvent plannerProcessEvent_${eventRecord.getData("processElement")}`,
        );
      }
    },
    eventTooltipFeature: {
      template: (data: { startDate: Date; endDate: Date; eventRecord: EventModel }) => {
        const processes = data.eventRecord.getData("processes") as (SimplifiedPlannerProcess & {
          name: string;
        })[];

        const html = `
          <h4 class="border-b pb-1 text-base">${data.eventRecord.name}</h4>
            <div class="flex flex-col gap-3 mt-2">
              <span class="text-sm plannedBg rounded px-1.5 py-0.5 w-min">${format(
                processes[0].start_time,
                "dd.MM.yyyy",
              )}</span>
              ${processes
                .map((process) => {
                  const duration = intervalToDuration({
                    start: process.end_time,
                    end: process.start_time,
                  });
                  const durationFormattedHours = (duration.hours || 0).toString().padStart(2, "0");
                  const durationFormattedMinutes = (duration.minutes || 0)
                    .toString()
                    .padStart(2, "0");

                  const name =
                    process.name ||
                    t(`report.sections.processes.process_type_entries.${process.encoded_label}`);

                  return `
                  <div class="flex flex-col gap-1 text-sm">
                    <span>
                      ${name}
                    </span>
                    <span style="font-weight: 300;" class="text-xs">
                      ${format(process.start_time, "HH:mm")} - ${format(process.end_time, "HH:mm")}
                      (${durationFormattedHours}:${durationFormattedMinutes}h)
                    </span>
                  </div>
                `;
                })
                .join("")}
            </div>`;

        return html;
      },
    },
    timeAxisHeaderMenuFeature: false,
    treeFeature: false,
    filterBarFeature: true,
    scheduleTooltipFeature: {
      generateTipContent(context: { date: Date; resourceRecord: ResourceModel }) {
        const name = context.resourceRecord.name;
        const formattedDate = format(context.date, "dd.MM.yyyy");
        const getOutages = context.resourceRecord.getData("getOutagesFn");
        const getHolidays = context.resourceRecord.getData("getHolidayFn");

        const outages = getOutages && getOutages(context.date);
        const holidays = getHolidays && getHolidays(context.date);

        const outagesTooltip = generateOutagesTooltip(outages, t);
        const holidaysTooltip = generateHolidayTooltip(holidays);

        return `
          <div class="flex flex-col gap-3">
            <h4 class="flex items-center ${(outagesTooltip || holidaysTooltip) && "border-b pb-1"}">
              <p class="mr-8">${name}</p>
              <p class="text-xs">${formattedDate}</p>
            </h4>
            ${
              outagesTooltip || holidaysTooltip
                ? `<div class="flex flex-col gap-3 mt-2>"
                  ${outagesTooltip}
                  ${holidaysTooltip}
                </div>`
                : ""
            }
            
          </div>`;
      },
    },
    headerMenuFeature: false,
    viewPreset: "customWeekAndDay",
    pdfExportFeature: {
      disabled: false,
      sendAsBinary: true,
      showErrorToast: false,
      fileFormat: "pdf",
      filterStyles(styles: string[]): string[] {
        return window.location.hostname === "localhost"
          ? styles.filter((style) => !style.includes("tailwindcss"))
          : styles;
      },
      paperFormat: "A4",
      orientation: "portrait",
      alignRows: false,
      repeatHeader: false,
      exporterType: "singlepage",
      fileName: `Prozessdaten_${projectName}.pdf`,
    },
    timeRangesFeature: {
      showCurrentTimeLine: {
        name: DateHelper.format(new Date(), "DD.MM.YYYY"),
        disabled: false,
      },
      headerRenderer({
        timeRange,
      }: {
        timeRange: { id: string | number; name: string; startDate: Date; endDate: Date };
      }) {
        if (typeof timeRange.id === "string" && timeRange.id.startsWith("holiday_")) {
          const startDate = timeRange.startDate;
          const endDate = subDays(timeRange.endDate, 1);
          const rangeLabel =
            startDate.getTime() === endDate.getTime()
              ? format(startDate, "dd.MM.yyyy")
              : `${format(startDate, "dd.MM.yyyy")}-${format(endDate, "dd.MM.yyyy")}`;
          return `
                <div class="planner-holiday-timerange-label-container tooltip">
                  <span class="planner-holiday-timerange-label">
                    ${timeRange.name}
                  </span>
                  <span class="tooltiptext tooltip-top">${timeRange.name} (${rangeLabel})</span>
                </div>
            `;
        }
        return `<span style="font-size: 12px">${timeRange.name}</span>`;
      },
    },
  };
};
